import * as yup from 'yup'

export const ServiceConfirmationValidationSchema2 = (
  t: any,
  hasDistanceInterval: boolean,
  vehicleOdometer: number | undefined,
  hasIoTData: boolean
) => {
  const belowVehicleOdometer = (value: number | null | undefined) => {
    if (value == null || vehicleOdometer == null) return true
    return value <= vehicleOdometer
  }

  return yup.object().shape({
    serviceDate: yup
      .date()
      .required(t('checks.validation.service_date_required', 'Service date is required'))
      .max(new Date(), t('checks.validation.service_date_no_future', 'Service date cannot be in the future')),
    serviceMeterSetting: hasDistanceInterval
      ? yup
        .number()
        .required(t('checks.validation.service_meter_required', 'Service meter setting is required for distance-based intervals'))
        .min(0, t('checks.validation.meter_positive', 'Meter setting must be positive'))
        .integer(t('checks.validation.meter_integer', 'Meter setting must be a whole number'))
        .test(
          'max-vehicle-odometer',
          t('checks.validation.service_meter_max_odometer', 'Service meter setting cannot exceed vehicle odometer reading'),
          function(this: yup.TestContext, value: number | null | undefined) {
            return belowVehicleOdometer(value)
          }
        )
      : yup.number().nullable(),
    currentMeterSetting: hasDistanceInterval && !hasIoTData
      ? yup
        .number()
        .required(t('checks.validation.current_meter_required', 'Current meter setting is required'))
        .min(0, t('checks.validation.meter_positive', 'Meter setting must be positive'))
        .integer(t('checks.validation.meter_integer', 'Meter setting must be a whole number'))
        .test(
          'max-vehicle-odometer',
          t('checks.validation.current_meter_max_odometer', 'Current meter setting cannot exceed vehicle odometer reading'),
          function(this: yup.TestContext, value: number | null | undefined) {
            return belowVehicleOdometer(value)
          }
        )
      : yup.number().nullable(),
    carWorkshopId: yup.number().nullable(),
    remark: yup.string().nullable().max(500, t('global.max_500_char', 'Remark must be less than 500 characters')),
  })
}

export const ServiceConfirmationValidationSchema = (t: any, hasDistanceInterval: boolean, currentOdometerReading?: number) => {

  return yup.object().shape({
    serviceDate: yup
      .date()
      .required(t('checks.validation.service_date_required', 'Service date is required'))
      .max(new Date(), t('checks.validation.service_date_no_future', 'Service date cannot be in the future')),
    serviceMeterSetting: hasDistanceInterval
      ? yup
        .number()
        .required(t('checks.validation.service_meter_required', 'Service meter setting is required for distance-based intervals'))
        .min(0, t('checks.validation.meter_positive', 'Meter setting must be positive'))
        .integer(t('checks.validation.meter_integer', 'Meter setting must be a whole number'))
        .test(
          'less-than-or-equal-current',
          t('checks.validation.current_meter_greater', 'Service meter setting must be less than or equal to current meter setting'),
          function(this: yup.TestContext, value: number | null | undefined) {
            const currentMeterSetting = this.parent.currentMeterSetting
            if (currentMeterSetting == null || currentMeterSetting === 0) return true
            if (value == null) return true
            return value <= currentMeterSetting
          }
        )
        .test(
          'max-odometer',
          t('checks.validation.service_meter_max_odometer', 'Service meter setting cannot exceed vehicle odometer reading'),
          function(this: yup.TestContext, value: number | null | undefined) {
            if (value == null) return true
            const vehicleOdometer = currentOdometerReading || 0
            const currentMeterSetting = this.parent.currentMeterSetting

            if (currentMeterSetting != null && currentMeterSetting >= value) {
              return true
            }

            return value <= vehicleOdometer
          }
        )
      : yup
        .number()
        .nullable(),
    currentMeterSetting: hasDistanceInterval
      ? yup
        .number()
        .nullable()
        .test(
          'valid-meter-setting',
          t('checks.validation.current_meter_greater2', 'Current meter setting must be greater than or equal to odometer reading'),
          function(this: yup.TestContext, value: number | null | undefined) {
            // Allow null or 0
            if (value == null || value === 0) return true

            // If value > 0, it must be >= vehicleOdometer
            const vehicleOdometer = currentOdometerReading || 0
            if (value < vehicleOdometer) return false

            return true
          }
        )
        .integer(t('checks.validation.meter_integer', 'Meter setting must be a whole number'))
      : yup
        .number()
        .nullable(),
    carWorkshopId: yup.number().nullable(),
    remark: yup.string().nullable().max(500, t('global.max_500_char', 'Remark must be less than 500 characters')),
  })
}
